import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, HelpCircleIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.8,
    ease: 'easeOut'
  }
};
const faqs = [{
  question: 'How do I book a consultation?',
  answer: 'You can book a consultation by calling our office at (+267) 316 5472, emailing us at charles@mcerickattorneys.co.bw, or using the contact form on our website. We also offer a convenient WhatsApp booking option via the floating button on your screen.'
}, {
  question: 'How long does a property transfer take?',
  answer: 'A standard property transfer in Botswana typically takes between 4 to 8 weeks from the date the deed of sale is signed, provided there are no complications. Factors that can affect this timeline include the speed of bond approval, obtaining rates clearance certificates, and processing times at the Deeds Registry.'
}, {
  question: 'What documents are required for a consultation?',
  answer: 'The required documents depend on your specific legal matter. Generally, we recommend bringing your Omang (ID) or passport, proof of residence, and any documents directly related to your case (e.g., contracts, correspondence, court summons, or property deeds). We will advise you on specific requirements when you book your appointment.'
}, {
  question: 'Do I need an appointment to see an attorney?',
  answer: 'Yes, we operate strictly by appointment to ensure that each client receives our undivided attention and that the appropriate attorney is available to assist you. However, in cases of genuine legal emergencies, we will do our utmost to accommodate you.'
}, {
  question: 'Do you assist businesses and corporations?',
  answer: 'Absolutely. A significant portion of our practice is dedicated to corporate and commercial law. We assist businesses of all sizes with company registration, commercial contracts, labour disputes, debt recovery, and general legal compliance.'
}, {
  question: 'How are your legal fees structured?',
  answer: 'Our fees are structured in accordance with the Law Society of Botswana guidelines. Depending on the matter, we may charge an hourly rate, a fixed fee (common for conveyancing and standard contracts), or a contingency fee in specific circumstances. We are transparent about our costs and will provide a clear fee estimate during your initial consultation.'
}];
export function FAQs() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <div className="w-full pt-24 bg-brand-offwhite" data-id="element-257">
      {/* Page Header */}
      <section className="relative py-20 bg-brand-charcoal text-white overflow-hidden" data-id="element-258">
        <div className="absolute inset-0 z-0 opacity-20" data-id="element-259">
          <img src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&q=80&w=2000" alt="Professionals in discussion" className="w-full h-full object-cover" data-id="element-260" />
        </div>
        <div className="relative z-10 max-w-container mx-auto px-6 text-center" data-id="element-261">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="font-serif text-5xl md:text-6xl font-bold mb-6" data-id="element-262">
            Frequently Asked Questions
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-263">
            Clear answers to common legal inquiries.
          </motion.p>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24" data-id="element-264">
        <div className="max-w-3xl mx-auto px-6" data-id="element-265">
          <motion.div {...fadeInUp} className="space-y-4" data-id="element-266">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-267">
                <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-268">
                  <span className="font-serif text-xl font-bold text-brand-charcoal pr-4" data-id="element-269">
                    {faq.question}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 text-brand-gold shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-270" />
                </button>
                <AnimatePresence data-id="element-271">
                  {openIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-272">
                      <div className="px-6 pb-6 text-gray-600 leading-relaxed border-t border-gray-50 pt-4" data-id="element-273">
                        {faq.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </motion.div>

          <motion.div {...fadeInUp} className="mt-16 text-center bg-white p-8 rounded-2xl shadow-soft border border-gray-100" data-id="element-274">
            <HelpCircleIcon className="w-12 h-12 text-brand-gold mx-auto mb-4" data-id="element-275" />
            <h3 className="font-serif text-2xl font-bold text-brand-charcoal mb-3" data-id="element-276">
              Still have questions?
            </h3>
            <p className="text-gray-600 mb-6" data-id="element-277">
              If you couldn't find the answer you were looking for, please don't
              hesitate to contact us directly.
            </p>
            <Link to="/contact" className="inline-block px-8 py-3 bg-brand-green hover:bg-brand-green-dark text-white rounded-full font-medium transition-colors" data-id="element-278">
              Contact Us
            </Link>
          </motion.div>
        </div>
      </section>
    </div>;
}